import { useEffect, useRef } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { ImpactItem } from "./editor/impact";

/**
 * The pre-save destructive-edit guard (Tech-decision "Destructive-edit guard", R5/FL-5). Before a save
 * that removes/retires tables or shrinks a standing zone, this BLOCKING dialog names exactly what is
 * being removed; the save proceeds only on acknowledgement. It turns a silent capacity drop into a
 * decision — the model still re-flags affected future bookings server-side (FloorPlanChanged → refit).
 */
export interface ImpactDialogProps {
  items: ImpactItem[];
  onConfirm: () => void;
  onCancel: () => void;
}

export function ImpactDialog({ items, onConfirm, onCancel }: ImpactDialogProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);

  function lineFor(item: ImpactItem): string {
    switch (item.kind) {
      case "TABLE_REMOVED":
        return t("floorPlan.impact.tableRemoved", { label: item.label });
      case "TABLE_RETIRED":
        return t("floorPlan.impact.tableRetired", { label: item.label });
      case "ZONE_REMOVED":
        return t("floorPlan.impact.zoneRemoved", { label: item.label });
      case "ZONE_CAPACITY_REDUCED":
        return t("floorPlan.impact.zoneReduced", {
          label: item.label,
          from: item.from,
          to: item.to,
        });
    }
  }

  return (
    <div
      className={css({
        position: "fixed",
        inset: "0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bg: "black/40",
        zIndex: "50",
        p: "4",
      })}
    >
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-label={t("floorPlan.impact.title")}
        tabIndex={-1}
        className={css({
          bg: "bg.default",
          rounded: "lg",
          borderWidth: "1px",
          p: "5",
          width: "full",
          maxW: "md",
          maxH: "85dvh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "3",
          outline: "none",
        })}
      >
        <h2 className={css({ fontSize: "lg", fontWeight: "bold" })}>
          {t("floorPlan.impact.title")}
        </h2>
        <p className={css({ fontSize: "sm" })}>{t("floorPlan.impact.intro")}</p>
        <ul
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "1",
            pl: "4",
            fontSize: "sm",
          })}
        >
          {items.map((item) => (
            <li key={`${item.kind}-${item.id}`}>{lineFor(item)}</li>
          ))}
        </ul>
        <p className={css({ fontSize: "xs", color: "fg.muted" })}>{t("floorPlan.impact.note")}</p>
        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end" })}>
          <button
            type="button"
            onClick={onCancel}
            className={css({ px: "3", py: "2", rounded: "md", borderWidth: "1px", fontSize: "sm" })}
          >
            {t("floorPlan.impact.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={css({
              px: "3",
              py: "2",
              rounded: "md",
              fontSize: "sm",
              bg: "colorPalette.default",
              color: "colorPalette.fg",
            })}
          >
            {t("floorPlan.impact.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
